// The order's attachments on the pallet desk (every order, whatever the
// carrier — Alex, 2026-10-21): each row names its kind and offers View
// (own tab) and Print; a hand-built carrier label can be attached here as
// kind shipping_label through the Orders attachments API.
import { useRef } from "react";
import { Eye, Paperclip, Printer } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { attachmentKind, type DeskAttachment, type DeskOrder } from "./palletDesk";
import { printAttachment, viewAttachment } from "./palletPrint";

const LABEL_ACCEPT = ".pdf,.png,.jpg,.jpeg";
const LABEL_MAX_BYTES = 10 * 1024 * 1024;

interface Props {
    order: DeskOrder;
    busy: boolean;
    readOnly: boolean;
    /** Wraps each action so the page's one-at-a-time lock applies. */
    run: (action: () => Promise<void>) => void;
    /** A refusal to report (the page's toast + sound). */
    fail: (code: string) => void;
    /** Something changed on the order — reload the desk. */
    onChanged: () => Promise<void>;
}

const PalletAttachments = ({ order, busy, readOnly, run, fail, onChanged }: Props) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachments = order.attachments ?? [];

    const upload = (file: File) => run(async () => {
        if (file.size > LABEL_MAX_BYTES) { toast.error(t("attachments_too_large"), { toastId: "pallet-label-size" }); return; }
        const form = new FormData();
        form.append("file", file);
        form.append("kind", "shipping_label");
        const res = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/attachments`, { method: "POST", body: form });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            playError();
            toast.error(body?.message || t("pallet_dispatch_upload_failed"), { toastId: "pallet-label-upload" });
            return;
        }
        playSuccess();
        toast.success(t("pallet_dispatch_uploaded", { name: file.name }), { toastId: `pallet-label-${order.order_id}` });
        await onChanged();
    });

    const print = (att: DeskAttachment) => run(async () => {
        const r = await printAttachment(order.order_id, att, t);
        if (!r.ok) fail(r.error);
    });
    const view = (att: DeskAttachment) => run(async () => {
        const r = await viewAttachment(order.order_id, att);
        if (!r.ok) fail(r.error);
    });

    return (
        <section className="pallet-attachments" aria-label={t("pallet_dispatch_attachments")}>
            <h3>{t("pallet_dispatch_attachments")}</h3>
            {attachments.length === 0 && <p className="pallet-dispatch-empty">{t("pallet_dispatch_no_attachments")}</p>}
            <ul>
                {attachments.map((att) => (
                    <li key={att.attachment_id}>
                        <span className="pallet-attachments-name">
                            <span className={`pallet-dispatch-kind pallet-dispatch-kind--${attachmentKind(att)}`}>{t(`pallet_dispatch_kind_${attachmentKind(att)}`)}</span>
                            {att.filename}
                        </span>
                        <span className="pallet-attachments-actions">
                            <button type="button" className="btn btn-sm" disabled={busy} aria-label={`${t("pallet_dispatch_view")} ${att.filename}`} onClick={() => view(att)}>
                                <Eye size={14} /> {t("pallet_dispatch_view")}
                            </button>
                            <button type="button" className="btn btn-sm" disabled={busy} aria-label={`${t("pallet_dispatch_print")} ${att.filename}`} onClick={() => print(att)}>
                                <Printer size={14} /> {t("pallet_dispatch_print")}
                            </button>
                        </span>
                    </li>
                ))}
            </ul>
            <input
                ref={fileInputRef}
                type="file"
                accept={LABEL_ACCEPT}
                aria-label={t("pallet_dispatch_upload_label")}
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) upload(file);
                }}
            />
            <button type="button" className="btn btn-sm" disabled={busy || readOnly} onClick={() => fileInputRef.current?.click()}>
                <Paperclip size={14} /> {t("pallet_dispatch_upload_label")}
            </button>
        </section>
    );
};

export default PalletAttachments;
